import React, { useEffect, useRef, useState } from "react";
import { MapPin } from "lucide-react";
import { api } from "@/lib/api";

export type AddrPick = { label: string; street: string; city: string; state: string; zip: string; county: string };

export function AddressAutocomplete({ value, onChange, onPick, testId, placeholder, id, required, minLength }: {
  value: string; onChange: (v: string) => void; onPick: (a: AddrPick) => void; testId: string; placeholder?: string; id?: string; required?: boolean; minLength?: number;
}) {
  const [items, setItems] = useState<AddrPick[]>([]);
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(-1);
  const typed = useRef(false);
  useEffect(() => {
    if (!typed.current || value.trim().length < 4) { setItems([]); return; }
    const t = setTimeout(() => {
      api.get("/public/address-suggest", { params: { q: value } }).then((r) => { setItems(r.data); setOpen(true); setHi(-1); }).catch(() => setItems([]));
    }, 300);
    return () => clearTimeout(t);
  }, [value]);
  const pick = (a: AddrPick) => { typed.current = false; setOpen(false); setItems([]); onPick(a); };
  const onKey = (e: React.KeyboardEvent) => {
    if (!open || !items.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => Math.min(items.length - 1, h + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(0, h - 1)); }
    else if (e.key === "Enter" && hi >= 0) { e.preventDefault(); pick(items[hi]); }
    else if (e.key === "Escape") setOpen(false);
  };
  return (
    <div className="relative">
      <input id={id} data-testid={testId} className="field-input" value={value} placeholder={placeholder} required={required} minLength={minLength} autoComplete="off"
        onChange={(e) => { typed.current = true; onChange(e.target.value); }} onKeyDown={onKey} onFocus={() => items.length && setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)} />
      {open && items.length > 0 && (
        <ul className="absolute z-40 mt-1 max-h-72 w-full overflow-auto rounded-xl border border-slate-200 bg-white py-1 shadow-lg" data-testid={`${testId}-suggestions`} role="listbox">
          {items.map((a, i) => (
            <li key={a.label} role="option" aria-selected={i === hi}>
              <button type="button" onMouseDown={(e) => { e.preventDefault(); pick(a); }} data-testid={`${testId}-suggestion-${i}`}
                className={`flex w-full items-start gap-2 px-3 py-2 text-left text-sm transition-colors ${i === hi ? "bg-[#F1F5FB]" : "hover:bg-[#F7F6F2]"}`}>
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[#0D9488]" />
                <span><span className="font-semibold text-[#1A2536]">{a.street}</span><span className="block text-xs text-[#64748B]">{[a.city, `${a.state} ${a.zip}`.trim(), a.county && `${a.county} County`].filter(Boolean).join(" · ")}</span></span>
              </button>
            </li>
          ))}
          <li className="px-3 pb-1 pt-1.5 text-[10px] text-[#94A3B8]">Suggestions © OpenStreetMap contributors. Check the house number.</li>
        </ul>
      )}
    </div>
  );
}
